import { useState } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { Button, InputSelect } from "../../ui";
import useMSAL from "../../../hooks/useMSAL";
import { QUEUE_REPORT_EXPORT } from "../../../graphql/mutation/ReportJob";
import {
  reportJobSection,
  reportSectionMeta,
} from "../../../modules/Reporting/utils";
import BrochureWizard from "./BrochureWizard";
import {
  buildExportPayload,
  DEFAULT_SHOW_ADDITIONAL_TRANSLATIONS,
  DEFAULT_INCLUDE_IMAGES,
  DEFAULT_INCLUDE_PREAMBLE,
  DEFAULT_REPORT_FORMAT,
  DEFAULT_REPORT_ORIENTATION,
  downloadBlobAsFile,
  fetchReportWithTokenRefresh,
  getDefaultReportForType,
  getReportFormatMeta,
  getReportMeta,
  getSupportedReportsForType,
  isQueuedReport,
  parseFilenameFromContentDisposition,
  readErrorMessage,
  REPORT_CATALOGS,
  supportsImages,
  supportsOrientation,
  supportsPreamble,
  supportsTranslationToggle,
  SUPPORTED_REPORT_FORMATS,
  SUPPORTED_REPORT_ORIENTATIONS,
} from "./utils";

const ORIENTATION_LABELS = {
  portrait: "Portrait",
  landscape: "Landscape",
};

const GenerateReportBody = props => {
  const {
    closeModal,
    ids = [],
    // Indirect brochure cohorts: the user selected artworks or exhibitions and
    // the wizard (and the server) expand those to instances.
    artworkIds = [],
    exhibitionIds = [],
    listId,
    search,
    select,
    type = "Contact",
    // Where the brochure wizard's "Show sales price" checkbox starts: off for
    // artwork-driven brochures, where the borrowed instance holds one of the
    // several prices the artwork has. The user can turn it back on.
    includePrices = true,
    // `report` is the *initial* selection — the user can change it via the
    // dropdown before clicking Generate. Defaults to the type's default
    // report so each entry point lands on something sensible.
    report: initialReport,
  } = props || {};

  const catalog = REPORT_CATALOGS[type] || REPORT_CATALOGS.Contact;
  const supportedReports = getSupportedReportsForType(type);
  const defaultReport = initialReport || getDefaultReportForType(type);

  const { requestAccessToken } = useMSAL();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(null);
  const [format, setFormat] = useState(DEFAULT_REPORT_FORMAT);
  const [report, setReport] = useState(defaultReport);
  const [includeImages, setIncludeImages] = useState(DEFAULT_INCLUDE_IMAGES);
  const [includePreamble, setIncludePreamble] = useState(
    DEFAULT_INCLUDE_PREAMBLE,
  );
  const [showAdditionalTranslations, setShowAdditionalTranslations] = useState(
    DEFAULT_SHOW_ADDITIONAL_TRANSLATIONS,
  );
  const [orientation, setOrientation] = useState(DEFAULT_REPORT_ORIENTATION);

  // Queued reports (the brochure) go through the `queueReportExport`
  // mutation + report worker instead of the synchronous download, so they
  // support large multi-entity cohorts. Progress lives in /reporting.
  const isQueued = isQueuedReport(report);
  // Which of the two queued-report sections will track this job.
  const queuedSection = reportSectionMeta(reportJobSection({ report }));
  const navigate = useNavigate();
  const [queueReportExport] = useMutation(QUEUE_REPORT_EXPORT);
  const [queuedJob, setQueuedJob] = useState(null);

  const isBrochure = type === "Instance" && report === "brochure";
  // An artwork / exhibition cohort is resolved by the wizard, which knows how
  // to expand it to instances.
  const hasIndirectCohort = artworkIds.length > 0 || exhibitionIds.length > 0;
  const brochureInvalidSelection =
    isBrochure &&
    ids.length === 0 &&
    !hasIndirectCohort &&
    !listId &&
    select !== "all";

  // Every brochure goes through the wizard, whether it covers one work or five
  // hundred: order the instances, arrange each one's pages and pick its images,
  // autosaving a draft along the way. A one-work brochure gets the same page
  // editor as the rest — it just has no order to arrange.
  const useBrochureWizard = isBrochure && !brochureInvalidSelection;

  const formatMeta = getReportFormatMeta(format);
  const reportMeta = getReportMeta(report);
  const defaultFilename = `${reportMeta.defaultFilenameSlug}.${formatMeta.extension}`;
  // Reports without artwork thumbnails (e.g. requests) hide the toggle:
  // there's nothing the flag could change, and showing the slow-with-many-
  // rows warning would be misleading.
  const reportSupportsImages = supportsImages(report);
  // The boilerplate preamble (left column under the invoice line items) can
  // be toggled off; payment terms and bank details fill that column instead.
  const reportSupportsPreamble = supportsPreamble(report);
  // The Invoice and Credit Note reports can show the additional (French)
  // translations; off by default the document prints in English only.
  const reportSupportsTranslationToggle = supportsTranslationToggle(report);
  // The Artwork Report can print portrait (default) or landscape; the
  // toggle is hidden for reports with a fixed orientation.
  const reportSupportsOrientation = supportsOrientation(report);
  // Format options scoped to this report — XLSX only when the catalog
  // declares a renderer for it. Today every Contact report has both
  // PDF + XLSX; the Artfair Price List is PDF-only.
  const reportFormats =
    Array.isArray(reportMeta.formats) && reportMeta.formats.length > 0
      ? reportMeta.formats
      : SUPPORTED_REPORT_FORMATS;

  const reportOptions = supportedReports.map(option => ({
    value: option,
    label: getReportMeta(option).menuLabel,
  }));
  const selectedReportOption = reportOptions.find(
    option => option.value === report,
  );

  const handleGenerate = async () => {
    if (brochureInvalidSelection) {
      setError("Select at least one instance to generate a PDF.");
      return;
    }

    setLoading(true);
    setError(null);
    setElapsedMs(null);

    const startedAt = performance.now();
    try {
      const payload = buildExportPayload({
        type,
        ids,
        artworkIds,
        exhibitionIds,
        listId,
        search,
        select,
        format,
        report,
        includeImages,
        includePreamble,
        includePrices,
        showAdditionalTranslations,
        orientation,
      });

      // Queued reports hand the payload to the report worker and let the
      // user track progress in the Reporting section — no blocking download.
      if (isQueued) {
        const { data } = await queueReportExport({
          variables: { input: payload },
        });
        const result = data?.queueReportExport;
        if (!result?.success) {
          const message = Array.isArray(result?.error)
            ? result.error.join(", ")
            : result?.error || "Failed to queue the report";
          throw new Error(message);
        }
        setQueuedJob(result.reportJob);
        setLoading(false);
        return;
      }

      const accept = formatMeta.accept;

      let token = localStorage.getItem("user:token");
      let response = await fetchReportWithTokenRefresh({
        token,
        payload,
        accept,
        requestAccessToken,
        onSessionExpired: () => window.location.reload(),
      });

      if (!response.ok) {
        const message = await readErrorMessage(response);
        throw new Error(message);
      }

      const blob = await response.blob();
      const filename =
        parseFilenameFromContentDisposition(
          response.headers.get("Content-Disposition"),
        ) || defaultFilename;

      downloadBlobAsFile({ blob, filename });
      setElapsedMs(Math.round(performance.now() - startedAt));
      closeModal?.();
    } catch (err) {
      console.error("[GenerateReport] failed", err);
      setError(err.message || `Failed to generate ${reportMeta.sentenceLabel}`);
    } finally {
      setLoading(false);
    }
  };

  const targetDescription = (() => {
    const noun = catalog.targetNoun;
    const nounPlural = catalog.targetNounPlural;
    if (select === "all" && listId) return `the saved list (id ${listId})`;
    if (select === "all")
      return `all ${nounPlural} matching the current search`;
    return `${ids.length} selected ${noun}${ids.length === 1 ? "" : "s"}`;
  })();

  if (useBrochureWizard) {
    return (
      <BrochureWizard
        closeModal={closeModal}
        ids={ids}
        artworkIds={artworkIds}
        exhibitionIds={exhibitionIds}
        listId={listId}
        includePrices={includePrices}
        search={search}
        select={select}
      />
    );
  }

  if (queuedJob) {
    return (
      <div className="mt-6 flex w-full flex-col">
        <div className="text-2xl font-bold">Report Queued</div>
        <div className="pt-4 text-base font-normal">
          Your {reportMeta.sentenceLabel} for {targetDescription} is being
          generated in the background. Track its progress and download the
          finished file from the {queuedSection.label} section.
        </div>
        <div className="flex flex-col-reverse gap-3 pt-6 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-4">
          <Button
            action="default"
            label="Close"
            onClick={() => closeModal?.()}
          />
          <Button
            label={`View in ${queuedSection.label}`}
            onClick={() => {
              closeModal?.();
              navigate(queuedSection.path);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 flex w-full flex-col">
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="text-2xl font-bold">Generate Report</div>
          <div className="pt-4 text-base font-normal">
            This will generate a {formatMeta.label} {reportMeta.sentenceLabel}{" "}
            for {targetDescription}.{" "}
            {isQueued
              ? `It will be generated in the background — track progress and download it from the ${queuedSection.label} section.`
              : "The file will download automatically when ready."}
          </div>
          <div className="w-full pt-4 sm:max-w-xs">
            <InputSelect
              label="Report"
              options={reportOptions}
              selected={selectedReportOption}
              disabled={loading}
              onChange={value => setReport(value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-4 text-sm">
            <span className="font-medium">Format:</span>
            {reportFormats.map(option => {
              const optionMeta = getReportFormatMeta(option);
              return (
                <label
                  key={option}
                  className="flex cursor-pointer items-center gap-1"
                >
                  <input
                    type="radio"
                    name="report-format"
                    value={option}
                    checked={format === option}
                    disabled={loading}
                    onChange={() => setFormat(option)}
                  />
                  <span>{optionMeta.label}</span>
                </label>
              );
            })}
          </div>
          {reportSupportsOrientation ? (
            <div className="flex flex-wrap items-center gap-3 pt-4 text-sm">
              <span className="font-medium">Orientation:</span>
              {SUPPORTED_REPORT_ORIENTATIONS.map(option => (
                <label
                  key={option}
                  className="flex cursor-pointer items-center gap-1"
                >
                  <input
                    type="radio"
                    name="report-orientation"
                    value={option}
                    checked={orientation === option}
                    disabled={loading}
                    onChange={() => setOrientation(option)}
                  />
                  <span>{ORIENTATION_LABELS[option] || option}</span>
                </label>
              ))}
            </div>
          ) : null}
          {brochureInvalidSelection ? (
            <div className="pt-4 text-sm text-red-600">
              Select at least one instance to generate a PDF.
            </div>
          ) : null}
          {reportSupportsImages ? (
            <div className="pt-4 text-sm">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  name="include-images"
                  checked={includeImages}
                  disabled={loading}
                  onChange={event => setIncludeImages(event.target.checked)}
                />
                <span className="font-medium">Include images</span>
              </label>
              {includeImages ? (
                <div className="mt-1 text-xs text-gray-500">
                  NOTE: including images can noticeably slow generation when
                  exporting a large number of {catalog.targetNounPlural}.
                </div>
              ) : null}
            </div>
          ) : null}
          {reportSupportsPreamble ? (
            <div className="pt-4 text-sm">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  name="include-preamble"
                  checked={includePreamble}
                  disabled={loading}
                  onChange={event => setIncludePreamble(event.target.checked)}
                />
                <span className="font-medium">Include Preamble</span>
              </label>
            </div>
          ) : null}
          {reportSupportsTranslationToggle ? (
            <div className="pt-4 text-sm">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  name="show-additional-translations"
                  checked={showAdditionalTranslations}
                  disabled={loading}
                  onChange={event =>
                    setShowAdditionalTranslations(event.target.checked)
                  }
                />
                <span className="font-medium">
                  Show additional translations
                </span>
              </label>
            </div>
          ) : null}
          {error ? (
            <div className="pt-4 text-sm text-red-600">{error}</div>
          ) : null}
          {elapsedMs != null ? (
            <div className="pt-4 text-sm text-gray-500">
              Generated in {elapsedMs}ms.
            </div>
          ) : null}
        </div>
        <div className="flex flex-col lg:shrink-0">
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              action="default"
              label="Cancel"
              disabled={loading}
              onClick={() => closeModal?.()}
            />
            <Button
              label={
                loading ? (isQueued ? "Queuing…" : "Generating…") : "Generate"
              }
              disabled={loading || brochureInvalidSelection}
              loading={loading}
              onClick={handleGenerate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { GenerateReportBody };
export default GenerateReportBody;
